import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plugs, Shield, Eye, EyeSlash, CheckCircle, XCircle, Warning,
  CaretRight, Gear, TestTube, Trash, Plus, ArrowClockwise,
  Key, Link as LinkIcon, Heartbeat, Lightning, Info, CaretDown,
  BookOpen, Copy
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Integration definitions with configuration requirements
const INTEGRATIONS = [
  {
    id: 'azure_ad',
    name: 'Azure Active Directory',
    description: 'User access and identity management. Import users, groups, and access policies.',
    icon: '🔷',
    color: '#0078D4',
    category: 'Identity',
    docsUrl: 'https://learn.microsoft.com/en-us/graph/auth-v2-service',
    fields: [
      { key: 'tenant_id', label: 'Tenant ID (Directory ID)', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
      { key: 'client_id', label: 'Client ID (Application ID)', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Client secret value', required: true }
    ],
    setupSteps: [
      'Go to Azure Portal → Microsoft Entra ID → App registrations → New registration',
      'Note the Application (client) ID and Directory (tenant) ID',
      'Go to Certificates & secrets → New client secret → Copy the secret value',
      'Go to API permissions → Add permission → Microsoft Graph → Application permissions',
      'Add: User.Read.All, Directory.Read.All, AuditLog.Read.All',
      'Click "Grant admin consent" for your organization'
    ],
    dataTypes: ['Users', 'Groups', 'Sign-in Logs', 'Audit Logs', 'Directory Roles']
  },
  {
    id: 'ms_sentinel',
    name: 'Microsoft Sentinel',
    description: 'Security incident and event management (SIEM). Import security alerts and incidents.',
    icon: '🛡️',
    color: '#0078D4',
    category: 'SIEM',
    docsUrl: 'https://learn.microsoft.com/en-us/rest/api/securityinsights/',
    fields: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text', placeholder: 'Azure AD tenant ID', required: true },
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Application (client) ID', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Client secret value', required: true },
      { key: 'subscription_id', label: 'Subscription ID', type: 'text', placeholder: 'Azure subscription ID', required: true },
      { key: 'resource_group', label: 'Resource Group', type: 'text', placeholder: 'Resource group name', required: true },
      { key: 'workspace_name', label: 'Workspace Name', type: 'text', placeholder: 'Log Analytics workspace name', required: true }
    ],
    setupSteps: [
      'Create an App Registration in Azure AD (same process as Azure AD integration)',
      'Assign "Microsoft Sentinel Reader" role to the app at the resource group or subscription level',
      'Note your Subscription ID from Azure Portal → Subscriptions',
      'Note your Resource Group name containing the Sentinel workspace',
      'Note your Log Analytics Workspace name',
      'Grant API permissions: Log Analytics API (Data.Read)'
    ],
    dataTypes: ['Incidents', 'Alerts', 'Hunting Queries', 'Threat Intelligence', 'Watchlists']
  },
  {
    id: 'ms_defender',
    name: 'Microsoft Defender',
    description: 'Endpoint protection and threat detection. Import vulnerability and threat data.',
    icon: '🔒',
    color: '#0078D4',
    category: 'Endpoint',
    docsUrl: 'https://learn.microsoft.com/en-us/microsoft-365/security/defender-endpoint/apis-intro',
    fields: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text', placeholder: 'Azure AD tenant ID', required: true },
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Application (client) ID', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Client secret value', required: true }
    ],
    setupSteps: [
      'Go to Azure Portal → Microsoft Entra ID → App registrations → New registration',
      'Note the Application (client) ID and Directory (tenant) ID',
      'Create a client secret and copy the value',
      'Go to API permissions → Add permission → APIs my organization uses → WindowsDefenderATP',
      'Add Application permissions: Alert.Read.All, Vulnerability.Read.All, Machine.Read.All',
      'Grant admin consent'
    ],
    dataTypes: ['Vulnerabilities', 'Alerts', 'Devices', 'Software Inventory', 'Security Recommendations']
  },
  {
    id: 'qualys',
    name: 'Qualys',
    description: 'Vulnerability management platform. Import vulnerability scans and asset data.',
    icon: '🔍',
    color: '#ED1C24',
    category: 'Vulnerability',
    docsUrl: 'https://docs.qualys.com/en/vm/api/',
    fields: [
      { key: 'api_url', label: 'API URL', type: 'text', placeholder: 'https://qualysapi.qualys.com', required: true },
      { key: 'username', label: 'Username', type: 'text', placeholder: 'Qualys username', required: true },
      { key: 'password', label: 'Password', type: 'password', placeholder: 'Qualys password', required: true }
    ],
    setupSteps: [
      'Log into Qualys and go to Administration → Users → User Actions → Edit',
      'Ensure "API" checkbox is enabled for the user',
      'Note your API URL based on your platform:',
      '  • US Platform 1: https://qualysapi.qualys.com',
      '  • US Platform 2: https://qualysapi.qg2.apps.qualys.com',
      '  • EU Platform: https://qualysapi.qualys.eu',
      '  • India Platform: https://qualysapi.qg3.apps.qualys.in',
      'Use your Qualys username and password'
    ],
    dataTypes: ['Vulnerabilities', 'Assets', 'Scan Reports', 'Compliance Data', 'Web App Findings']
  },
  {
    id: 'servicenow',
    name: 'ServiceNow',
    description: 'IT service management and incident tracking. Import incidents and change requests.',
    icon: '📋',
    color: '#81B5A1',
    category: 'ITSM',
    docsUrl: 'https://docs.servicenow.com/bundle/tokyo-api-reference/page/integrate/inbound-rest/concept/c_RESTAPI.html',
    fields: [
      { key: 'instance_url', label: 'Instance URL', type: 'text', placeholder: 'https://your-instance.service-now.com', required: true },
      { key: 'username', label: 'Username', type: 'text', placeholder: 'ServiceNow username', required: true },
      { key: 'password', label: 'Password', type: 'password', placeholder: 'ServiceNow password', required: true }
    ],
    setupSteps: [
      'Your instance URL format: https://your-instance.service-now.com',
      'Use your ServiceNow username and password',
      'Required roles: itil, rest_api_explorer, or appropriate table access',
      'For OAuth (optional): Go to System OAuth → Application Registry → New',
      'Create "OAuth API endpoint for external clients"',
      'Note the Client ID and Client Secret'
    ],
    dataTypes: ['Incidents', 'Changes', 'Problems', 'CMDB Assets', 'Security Incidents']
  }
];

// Integration Card Component
const IntegrationCard = ({ integration, config, onConfigure, onTest, onDelete }) => {
  const isConnected = config?.status === 'connected';
  const isPending = config?.status === 'pending';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-background-surface border rounded-lg p-5 ${
        isConnected ? 'border-emerald-500/30' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${integration.color}15` }}
          >
            {integration.icon}
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-primary">{integration.name}</h3>
            <span className="text-xs text-text-tertiary">{integration.category}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded">
              <CheckCircle size={12} weight="fill" />
              Connected
            </span>
          ) : isPending ? (
            <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 text-amber-400 text-xs rounded">
              <Warning size={12} />
              Pending
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-1 bg-gray-500/10 text-text-tertiary text-xs rounded">
              <XCircle size={12} />
              Not Connected
            </span>
          )}
        </div>
      </div>
      
      <p className="text-xs text-text-secondary mb-4">{integration.description}</p>
      
      {/* Data Types */}
      <div className="flex flex-wrap gap-1 mb-4">
        {integration.dataTypes.map((type) => (
          <span key={type} className="px-2 py-0.5 bg-background text-text-tertiary text-[10px] rounded">
            {type}
          </span>
        ))}
      </div>
      
      {/* Last Sync Info */}
      {config?.last_sync && (
        <div className="text-xs text-text-tertiary mb-4 flex items-center gap-1">
          <ArrowClockwise size={12} />
          Last sync: {new Date(config.last_sync).toLocaleString()}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onConfigure(integration)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gold/10 border border-gold/30 text-gold text-xs rounded hover:bg-gold/20 transition-colors"
        >
          <Gear size={14} />
          {isConnected ? 'Reconfigure' : 'Configure'}
        </button>
        {isConnected && (
          <>
            <button
              onClick={() => onTest(integration.id)}
              className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs rounded hover:bg-blue-500/20 transition-colors"
            >
              <TestTube size={14} />
              Test
            </button>
            <button
              onClick={() => onDelete(integration.id)}
              className="flex items-center justify-center p-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded hover:bg-red-500/20 transition-colors"
            >
              <Trash size={14} />
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};

// Configuration Modal
const ConfigurationModal = ({ integration, existingConfig, onSave, onClose }) => {
  const [formData, setFormData] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSetupSteps, setShowSetupSteps] = useState(false);
  
  useEffect(() => {
    if (existingConfig?.credentials) {
      setFormData(existingConfig.credentials);
    }
  }, [existingConfig]);
  
  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setTestResult(null);
  };
  
  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post(`${API}/integrations/${integration.id}/test`, {
        credentials: formData
      });
      
      if (response.data.success) {
        setTestResult({ 
          success: true, 
          message: response.data.message || 'Connection successful!',
          details: response.data.details
        });
      } else {
        setTestResult({ 
          success: false, 
          message: response.data.message || 'Connection failed',
          details: response.data.details
        });
      }
    } catch (error) {
      const errorData = error.response?.data;
      setTestResult({ 
        success: false, 
        message: errorData?.message || errorData?.detail || 'Connection test failed. Please check your credentials.',
        details: errorData?.details
      });
    } finally {
      setTesting(false);
    }
  };
  
  const handleSave = () => {
    // Validate required fields
    const missingFields = integration.fields.filter(f => f.required && !formData[f.key]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }
    
    onSave(integration.id, formData);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background-surface border border-border rounded-lg w-full max-w-2xl my-8"
      >
        <div className="p-5 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                style={{ backgroundColor: `${integration.color}15` }}
              >
                {integration.icon}
              </div>
              <div>
                <h3 className="text-lg font-medium text-text-primary">Configure {integration.name}</h3>
                <p className="text-xs text-text-tertiary">{integration.category}</p>
              </div>
            </div>
            {integration.docsUrl && (
              <a
                href={integration.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-gold hover:underline"
              >
                <BookOpen size={14} />
                Documentation
              </a>
            )}
          </div>
        </div>
        
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Setup Steps Toggle */}
          {integration.setupSteps && (
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
              <button
                onClick={() => setShowSetupSteps(!showSetupSteps)}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <Info size={16} className="text-blue-400" />
                  <span className="text-sm font-medium text-blue-400">Setup Instructions</span>
                </div>
                <CaretDown size={14} className={`text-blue-400 transition-transform ${showSetupSteps ? 'rotate-180' : ''}`} />
              </button>
              
              {showSetupSteps && (
                <div className="mt-3 pt-3 border-t border-blue-500/20">
                  <ol className="space-y-2">
                    {integration.setupSteps.map((step, idx) => (
                      <li key={idx} className="flex gap-2 text-xs text-text-secondary">
                        <span className="text-blue-400 font-mono">{idx + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
          
          {/* Credential Fields */}
          {integration.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-xs text-text-secondary mb-1">
                {field.label}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </label>
              <div className="relative">
                <input
                  type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                  value={formData[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 bg-background border border-border rounded text-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none pr-10 font-mono"
                />
                {field.type === 'password' && (
                  <button
                    type="button"
                    onClick={() => setShowSecrets(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                  >
                    {showSecrets[field.key] ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg ${testResult.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle size={18} className="text-emerald-400 mt-0.5" weight="fill" />
                ) : (
                  <XCircle size={18} className="text-red-400 mt-0.5" weight="fill" />
                )}
                <div className="flex-1">
                  <span className={`text-sm font-medium ${testResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {testResult.message}
                  </span>
                  
                  {/* Connection Details */}
                  {testResult.details && typeof testResult.details === 'object' && !Array.isArray(testResult.details) && (
                    <div className="mt-2 space-y-1">
                      {Object.entries(testResult.details).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2 text-xs">
                          <span className="text-text-tertiary">{key.replace(/_/g, ' ')}:</span>
                          <span className="text-text-secondary font-mono">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Error Details (array of strings) */}
                  {testResult.details && Array.isArray(testResult.details) && (
                    <ul className="mt-2 space-y-1">
                      {testResult.details.map((detail, idx) => (
                        <li key={idx} className="text-xs text-text-tertiary flex items-start gap-1">
                          <span>•</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-5 border-t border-border flex items-center justify-between">
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm rounded hover:bg-blue-500/20 transition-colors disabled:opacity-50"
          >
            {testing ? (
              <>
                <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                Testing Connection...
              </>
            ) : (
              <>
                <TestTube size={16} />
                Test Connection
              </>
            )}
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-gold text-gold-foreground text-sm rounded hover:bg-gold-hover transition-colors"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Main Page Component
const IntegrationsSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [configurations, setConfigurations] = useState({});
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  
  const fetchConfigurations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/configurations`);
      const configMap = {};
      (response.data.integrations || []).forEach(config => {
        configMap[config.integration_id] = config;
      });
      setConfigurations(configMap);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);
  
  const handleConfigure = (integration) => {
    setSelectedIntegration(integration);
    setShowConfigModal(true);
  };
  
  const handleSaveConfiguration = async (integrationId, credentials) => {
    try {
      await axios.post(`${API}/integrations/${integrationId}/configure`, {
        credentials
      });
      
      toast.success('Configuration saved');
      setShowConfigModal(false);
      setSelectedIntegration(null);
      fetchConfigurations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };
  
  const handleTestConnection = async (integrationId) => {
    try {
      const response = await axios.post(`${API}/integrations/${integrationId}/test`);
      if (response.data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error(response.data.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    }
  };
  
  const handleDeleteConfiguration = async (integrationId) => {
    if (!confirm('Are you sure you want to remove this integration?')) return;
    
    try {
      await axios.delete(`${API}/integrations/${integrationId}`);
      toast.success('Integration removed');
      fetchConfigurations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove integration');
    }
  };
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            <span className="text-sm text-text-tertiary">Loading Integrations...</span>
          </div>
        </div>
      </div>
    );
  }
  
  const connectedCount = Object.values(configurations).filter(c => c.status === 'connected').length;
  
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary mb-1">
                Enterprise Integrations
              </h1>
              <p className="text-sm text-text-secondary">
                Connect your security tools for automated data collection and audit intelligence
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-2xl font-mono font-semibold text-gold">{connectedCount}</div>
                <div className="text-xs text-text-tertiary">Connected</div>
              </div>
              <div className="w-px h-10 bg-border" />
              <div className="text-right">
                <div className="text-2xl font-mono font-semibold text-text-primary">{INTEGRATIONS.length}</div>
                <div className="text-xs text-text-tertiary">Available</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Info Banner */}
        <div className="p-6 border-b border-border bg-purple-500/5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Lightning size={20} className="text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-text-primary mb-1">Audit Intelligence</h3>
              <p className="text-xs text-text-secondary">
                Connected integrations automatically feed high-risk incidents, vulnerabilities, and security events 
                into the Audit Intelligence system. NAVI will analyze this data to provide actionable insights 
                for your compliance audits.
              </p>
            </div>
          </div>
        </div>
        
        {/* Integrations Grid */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            {INTEGRATIONS.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                config={configurations[integration.id]}
                onConfigure={handleConfigure}
                onTest={handleTestConnection}
                onDelete={handleDeleteConfiguration}
              />
            ))}
          </div>
        </div>
        
        {/* Configuration Modal */}
        {showConfigModal && selectedIntegration && (
          <ConfigurationModal
            integration={selectedIntegration}
            existingConfig={configurations[selectedIntegration.id]}
            onSave={handleSaveConfiguration}
            onClose={() => { setShowConfigModal(false); setSelectedIntegration(null); }}
          />
        )}
      </div>
    </div>
  );
};

export default IntegrationsSettingsPage;
